import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { Store } from '@ngrx/store';
import { parse } from 'date-fns';

import { map, switchMap, take, tap } from 'rxjs/operators';
import { BeneficiaryEffectiveDate } from 'src/app/modules/dados-cadastrais/models/BeneficiaryEffectiveDate';
import { BeneficiaryPlan } from 'src/app/modules/dados-cadastrais/models/BeneficiaryPlan';
import { endpoints } from 'src/environments/endpoints';
import { Beneficiary } from '../../models/Beneficiary';
import { BeneficiaryBlockReason } from '../../models/BeneficiaryBlockReason';
import { BeneficiaryCardNumber } from '../../models/BeneficiaryCardNumber';
import { BeneficiaryRule } from '../../models/BeneficiaryRule';
import { BenefitType } from '../../models/BenefitType';
import { BlockBeneficiaryPayload } from '../../models/BlockBeneficiaryPayload';
import { CreateBeneficiaryPayload } from '../../models/CreateBeneficiaryPayload';
import { SimpleBeneficiary } from '../../models/SimpleBeneficiary';
import { IAppState } from '../../store/interfaces/IAppState';
import { selectUser, selectUserType } from '../../store/selectors';
import { UploadService } from '../upload/upload.service';

export interface ListBeneficiariesParams {
  benefitType?: string;
  flat?: string;
  idClient?: string;
  name?: string;
  contract?: string;
  subcontract?: string;
  segmento?: string;
  operadora?: string;
}

@Injectable({
  providedIn: 'root',
})
export class BeneficiaryService {
  constructor(private http: HttpClient, private store: Store<IAppState>, private uploadService: UploadService) {}

  getBeneficiaries({ benefitType, idClient, name, flat, contract, subcontract, segmento, operadora }: ListBeneficiariesParams): Observable<SimpleBeneficiary[]> {
    const url = endpoints.beneficiaries;

    return this.store.select(selectUserType).pipe(
      take(1),
      switchMap((userType) => {
        const params: any = {
          typePerson: userType === 'PJ' ? 'j' : 'f',
        };

        if (idClient) {
          params.idClient = idClient;
        }

        if (name) {
          params.name = name;
        }

        if (benefitType) {
          params.benefitType = benefitType;
        }

        if (idClient) {
          params.idClient = idClient;
        }

        if (flat) {
          params.flat = flat;
        }

        if (contract) {
          params.contract = contract;
        }
        if (subcontract) {
          params.subcontract = subcontract;
        }
        if (segmento) {
          params.segmento = segmento;
        }
        if (operadora) {
          params.operadora = operadora;
        }

        return this.http.get<{ beneficiaries: SimpleBeneficiary[] }>(url, { params });
      }),
      map((response) => response?.beneficiaries)
    );
  }

  updateBeneficiary(beneficiary: Beneficiary): Observable<void> {
    const baseURL = endpoints.beneficiaries;

    return this.store.select(selectUser).pipe(
      take(1),
      switchMap((user) =>
        this.http.put<void>(baseURL, {
          beneficiary,
          idClient: user?.idClient,
        })
      )
    );
  }

  blockBeneficiary(payload: BlockBeneficiaryPayload): Observable<void> {
    const baseURL = `${endpoints.beneficiaries}/block-beneficiary`;
    return this.http.post<void>(baseURL, payload);
  }

  getHolders(): Observable<SimpleBeneficiary[]> {
    return this.getBeneficiaries({ benefitType: '1' }).pipe(map((r) => r.filter((b) => b.kinship === 'Titular')));
  }

  getDependents(holderId: string): Observable<SimpleBeneficiary[]> {
    return this.getBeneficiaries({ benefitType: '1' }).pipe(
      map((r) =>
        r.filter((b) => {
          return b.idBeneficiary.substr(0, 26) === holderId.substr(0, 26);
        })
      )
    );
  }

  findBeneficiaryById(id: string): Observable<Beneficiary> {
    const baseURL = endpoints.beneficiaries;
    const url = `${baseURL}/${id}`;

    return this.http.get<Beneficiary>(url);
  }

  create(payload: CreateBeneficiaryPayload, documents: File[]): Observable<void> {
    const url = endpoints.beneficiaries;

    if (!documents.length) {
      return this.http.post<void>(url, payload);
    }

    return this.uploadService.uploadFiles(documents).pipe(
      tap((files) =>
        files.forEach((f, i) => {
          payload.documents[i].url = this.extractIdFromBlobImage(f);
        })
      ),
      switchMap(() => this.http.post<void>(url, payload))
    );
  }

  getCardNumbers(cgc: string, birthDate: string): Observable<BeneficiaryCardNumber[]> {
    const baseURL = endpoints.beneficiaries;
    const url = `${baseURL}/list-cardnumber`;

    const params: any = {
      cgc,
    };

    if (birthDate) {
      params.birthDate = parse(birthDate, 'dd/MM/yyyy', 0).toISOString();
    }

    return this.http.get<BeneficiaryCardNumber[]>(url, { params });
  }

  getBenefitTypes(distinct = true): Observable<BenefitType[]> {
    const baseURL = endpoints.beneficiaries;
    const url = `${baseURL}/list-benefit-types`;

    return this.http.get<BenefitType[]>(url).pipe(
      map((v) => v.sort((a, l) => (+a.type > +l.type ? 1 : +a.type < +l.type ? -1 : 0))),
      map((v) =>
        distinct
          ? v
              .map((b) => ({
                ...b,
                description: b.description.split('-')[0],
              }))
              .reduce((acc, item) => {
                if (!acc.find((i) => i.description === item.description)) {
                  return [...acc, item];
                }
                return acc;
              }, [] as BenefitType[])
          : v
      )
    );
  }

  getBeneficiaryBlock(idBeneficiary?: string): Observable<BlockBeneficiaryPayload> {
    const baseURL = endpoints.beneficiaries;
    const url = `${baseURL}/list-block-beneficiary/${idBeneficiary}`;
    return this.http.get<BlockBeneficiaryPayload>(url);
  }

  getEffectiveDates(idBeneficiary?: string): Observable<BeneficiaryEffectiveDate> {
    const baseURL = endpoints.beneficiaries;
    const url = `${baseURL}/effective-date`;

    let params = new HttpParams();

    if (idBeneficiary) {
      params = params.append('idBeneficiary', idBeneficiary);
    }

    return this.http.get<BeneficiaryEffectiveDate>(url, { params });
  }

  /**
   * Lista os cargos disponíveis
   * 
   * @returns Retorna uma lista observável de Cargos
   */
  loadRules(): Observable<BeneficiaryRule[]> {
    const baseURL = endpoints.beneficiaries;
    const url = `${baseURL}/list-cargos`;

    return this.http.get<BeneficiaryRule[]>(url);
  }

  loadBlockReaons(tipo: number): Observable<BeneficiaryBlockReason> {
    const baseURL = endpoints.beneficiaries;
    const url = `${baseURL}/list-block-reasons/${tipo}`;

    return this.http.get<BeneficiaryBlockReason>(url);
  }

  updateBeneficiaryPlan(beneficiaryPlan: BeneficiaryPlan): Observable<BeneficiaryPlan> {
    const baseURL = endpoints.beneficiaries;
    const url = `${baseURL}/update-beneficiary-plan`;

    return this.http.put<BeneficiaryPlan>(url, beneficiaryPlan);
  }

  private extractIdFromBlobImage(blobImageUrl: string): string {
    return blobImageUrl.slice(blobImageUrl.lastIndexOf('/') + 1);
  }

}
